'use client';

// P29.10 marketing testimonial carousel. Auto-rotates every 6s; pauses on
// hover. Manual prev/next chevrons. framer-motion AnimatePresence slide.
// Placeholder testimonials (in messages/it.json); real quotes when beta
// operators agree.

import { useEffect, useState } from 'react';
import { AnimatePresence, m, useReducedMotion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const ROTATE_MS = 6000;

type Item = { quote: string; author: string; title: string; company: string };

export function TestimonialCarousel() {
  const t = useTranslations('marketing.testimonial');
  const items = t.raw('items') as Item[];
  const reduceMotion = useReducedMotion();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    const id = window.setInterval(() => {
      setIdx((i) => (i + 1) % items.length);
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [paused, items.length]);

  if (!items || items.length === 0) return null;
  const current = items[idx] ?? items[0]!;

  const variants = reduceMotion
    ? { initial: { opacity: 1 }, animate: { opacity: 1 }, exit: { opacity: 1 } }
    : {
        initial: { opacity: 0, x: 24 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: -24 },
      };

  return (
    <section
      data-testid="marketing-testimonial"
      className="bg-emerald-50 py-20"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mx-auto max-w-3xl px-4 text-center">
        <h2 className="text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
          {t('sectionTitle')}
        </h2>

        <div className="relative mt-10">
          <Quote className="mx-auto h-8 w-8 text-emerald-700/40" aria-hidden />
          <AnimatePresence mode="wait">
            <m.blockquote
              key={idx}
              data-testid={`testimonial-${idx}`}
              initial={variants.initial}
              animate={variants.animate}
              exit={variants.exit}
              transition={{ duration: 0.3 }}
              className="mt-4 text-lg leading-relaxed text-slate-700 italic md:text-xl"
            >
              “{current.quote}”
            </m.blockquote>
          </AnimatePresence>
          <p className="mt-6 text-sm font-semibold text-slate-900">{current.author}</p>
          <p className="text-xs text-slate-500">
            {current.title} — {current.company}
          </p>

          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              type="button"
              data-testid="testimonial-prev"
              aria-label={t('prevAria')}
              onClick={() => setIdx((i) => (i - 1 + items.length) % items.length)}
              className="rounded-full border border-slate-200 bg-white p-1.5 text-slate-600 hover:text-emerald-700"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </button>
            <div className="flex items-center gap-1">
              {items.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 w-6 rounded-full ${
                    i === idx ? 'bg-emerald-700' : 'bg-emerald-200'
                  }`}
                />
              ))}
            </div>
            <button
              type="button"
              data-testid="testimonial-next"
              aria-label={t('nextAria')}
              onClick={() => setIdx((i) => (i + 1) % items.length)}
              className="rounded-full border border-slate-200 bg-white p-1.5 text-slate-600 hover:text-emerald-700"
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
